import { Skeleton } from "@/components/ui/skeleton";
import { Button } from "@medusajs/ui";
import { createRoute } from "@tanstack/react-router";
import { MapPin, Plus } from "lucide-react";
import { useCallback, useMemo, useState } from "react";
import { accountRoute } from "./account-page";
import { useReportLoadFailure } from "@/hooks/useReportLoadFailure";
import { useAddresses, useUpdateAddress } from "@/queries/customer/addresses";
import type { HttpTypes } from "@medusajs/types";
import {
  AddressCard,
  AddressDialog,
  DeleteAddressDialog
} from "@/components/addresses";

export const accountAddressesRoute = createRoute({
  getParentRoute: () => accountRoute,
  path: "addresses",
  component: AccountAddressesPage
});

/**
 * Addresses management page component
 *
 * Features:
 * - List all saved addresses
 * - Add new address via modal dialog
 * - Edit existing addresses
 * - Delete addresses with confirmation
 * - Set address as default shipping and/or billing
 * - Loading and empty states
 * - Accessible and responsive layout
 */
function AccountAddressesPage() {
  const { data, isLoading, error } = useAddresses();
  useReportLoadFailure(error, "addresses page");
  const addresses = data?.addresses || [];
  const updateAddressMutation = useUpdateAddress();

  // Find default addresses by looking for the one with is_default flag set to true
  const defaultShippingAddressId = useMemo(
    () => addresses.find((addr) => addr.is_default_shipping)?.id,
    [addresses]
  );
  const defaultBillingAddressId = useMemo(
    () => addresses.find((addr) => addr.is_default_billing)?.id,
    [addresses]
  );

  // Check if this is the first address (used to auto-set both defaults)
  const isFirstAddress = addresses.length === 0;

  // Dialog state
  const [isAddressDialogOpen, setIsAddressDialogOpen] = useState(false);
  const [editingAddress, setEditingAddress] =
    useState<HttpTypes.StoreCustomerAddress | null>(null);
  const [deletingAddressId, setDeletingAddressId] = useState<string | null>(
    null
  );
  const [deletingAddressName, setDeletingAddressName] = useState<
    string | undefined
  >(undefined);

  // Handlers
  const handleAddNew = useCallback(() => {
    setEditingAddress(null);
    setIsAddressDialogOpen(true);
  }, []);

  const handleEdit = useCallback((address: HttpTypes.StoreCustomerAddress) => {
    setEditingAddress(address);
    setIsAddressDialogOpen(true);
  }, []);

  const handleDelete = useCallback(
    (addressId: string) => {
      const address = addresses.find((a) => a.id === addressId);
      setDeletingAddressId(addressId);
      setDeletingAddressName(address?.address_name || undefined);
    },
    [addresses]
  );

  const handleDialogClose = useCallback((open: boolean) => {
    if (!open) {
      setIsAddressDialogOpen(false);
      setEditingAddress(null);
    }
  }, []);

  const handleDeleteDialogClose = useCallback((open: boolean) => {
    if (!open) {
      setDeletingAddressId(null);
      setDeletingAddressName(undefined);
    }
  }, []);

  const handleSetDefaultShipping = useCallback(
    (addressId: string) => {
      updateAddressMutation.mutate({
        addressId,
        address: { is_default_shipping: true }
      });
    },
    [updateAddressMutation]
  );

  const handleSetDefaultBilling = useCallback(
    (addressId: string) => {
      updateAddressMutation.mutate({
        addressId,
        address: { is_default_billing: true }
      });
    },
    [updateAddressMutation]
  );

  return (
    <div>
      {/* Page Header */}
      <div className="border-border flex flex-col gap-4 border-b p-6 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h2
            className="text-xl font-semibold text-gray-900 dark:text-white"
            id="addresses-heading"
          >
            Addresses
          </h2>
          <p
            className="mt-1 text-sm text-gray-600 dark:text-gray-400"
            id="addresses-description"
          >
            Manage your shipping and billing addresses
          </p>
        </div>
        <Button
          onClick={handleAddNew}
          className="self-start sm:self-auto"
          aria-label="Add new address"
        >
          <Plus className="mr-2 h-4 w-4" aria-hidden="true" />
          Add Address
        </Button>
      </div>

      {/* Content */}
      <div className="p-6">
        <section
          aria-labelledby="addresses-heading"
          aria-describedby="addresses-description"
        >
          {/* Loading State */}
          {isLoading && <AddressesLoadingSkeleton />}

          {/* Error State */}
          {error && !isLoading && (
            <div className="rounded-lg border border-red-200 bg-red-50 p-4 dark:border-red-800 dark:bg-red-950">
              <div className="flex flex-col items-center justify-center text-center">
                <p className="text-lg font-medium text-red-600 dark:text-red-400">
                  Failed to load addresses
                </p>
                <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
                  We couldn&apos;t load your addresses. Please try again later.
                </p>
              </div>
            </div>
          )}

          {/* Empty State */}
          {!isLoading && !error && addresses.length === 0 && (
            <div
              className="flex min-h-[250px] flex-col items-center justify-center rounded-lg border border-dashed border-gray-300 dark:border-gray-700"
              role="status"
              aria-live="polite"
            >
              <MapPin
                className="mb-4 h-12 w-12 text-gray-400"
                aria-hidden="true"
              />
              <p className="text-lg font-medium text-gray-900 dark:text-white">
                No addresses yet
              </p>
              <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
                Add your first address to make checkout faster
              </p>
              <Button onClick={handleAddNew} className="mt-4">
                <Plus className="mr-2 h-4 w-4" aria-hidden="true" />
                Add your first address
              </Button>
            </div>
          )}

          {/* Addresses Grid */}
          {!isLoading && !error && addresses.length > 0 && (
            <div
              className="grid grid-cols-1 gap-4 sm:grid-cols-2"
              role="list"
              aria-label="Saved addresses"
            >
              {addresses.map((address) => (
                <div key={address.id} role="listitem">
                  <AddressCard
                    address={address}
                    onEdit={handleEdit}
                    onDelete={handleDelete}
                    onSetDefaultShipping={handleSetDefaultShipping}
                    onSetDefaultBilling={handleSetDefaultBilling}
                    isDefaultShipping={address.id === defaultShippingAddressId}
                    isDefaultBilling={address.id === defaultBillingAddressId}
                    disabled={updateAddressMutation.isPending}
                  />
                </div>
              ))}
            </div>
          )}
        </section>
      </div>

      {/* Add/Edit Address Dialog */}
      <AddressDialog
        open={isAddressDialogOpen}
        onOpenChange={handleDialogClose}
        address={editingAddress}
        isFirstAddress={isFirstAddress}
      />

      {/* Delete Confirmation Dialog */}
      <DeleteAddressDialog
        open={!!deletingAddressId}
        onOpenChange={handleDeleteDialogClose}
        addressId={deletingAddressId}
        addressName={deletingAddressName}
      />
    </div>
  );
}

/**
 * Loading skeleton for addresses list
 */
function AddressesLoadingSkeleton() {
  return (
    <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
      {[1, 2].map((i) => (
        <div
          key={i}
          className="border-border bg-card rounded-lg border p-4 shadow-sm"
        >
          <div className="flex items-start justify-between">
            <div className="space-y-2">
              <Skeleton className="h-5 w-32" />
              <Skeleton className="h-4 w-48" />
              <Skeleton className="h-4 w-40" />
              <Skeleton className="h-4 w-24" />
            </div>
            <Skeleton className="h-8 w-24 rounded" />
          </div>
        </div>
      ))}
    </div>
  );
}
